/**
 * Client-side allocation summary currents.
 * Cost/category must be resolved per (emp_id, training_id) — never a global training_id map
 * (same training can be Category B for unemployed and Category D for employed).
 */

function normalizeCategory(raw) {
  if (!raw) return "";
  const m = String(raw).match(/category\s*([a-g])/i);
  if (m) return m[1].toUpperCase();
  return String(raw).trim().toUpperCase().slice(0, 1);
}

function resolveEmpTrainingCostAndCategory(emp, trainingId, master) {
  const selected = (emp?.training_selected || []).find((t) => {
    if (!t) return false;
    if (typeof t === "object" && !Array.isArray(t)) {
      return String(t.training_id) === String(trainingId);
    }
    return String(t[0]) === String(trainingId);
  });
  const masterRow = (master || []).find(
    (t) => String(t.training_id) === String(trainingId),
  );
  let cost = 0;
  let category = "";
  if (selected && typeof selected === "object" && !Array.isArray(selected)) {
    cost =
      selected.cost != null && !Number.isNaN(Number(selected.cost))
        ? Number(selected.cost)
        : Number(masterRow?.cost ?? 0);
    category = normalizeCategory(selected.training_category);
  } else if (Array.isArray(selected)) {
    cost = Number(masterRow?.cost ?? 0);
    category = normalizeCategory(selected[1]);
  } else {
    cost = Number(masterRow?.cost ?? 0);
  }
  return { cost, category };
}

function lookupBbeeTarget(bbeeValues, race, gender) {
  if (!bbeeValues) return 0;
  const key =
    Object.keys(bbeeValues).find((k) => k.toLowerCase() === race.toLowerCase()) ||
    race;
  const group = bbeeValues[key];
  return Number(group?.[gender]?.c ?? 0);
}

function lookupCategoryTarget(trainingCategoryValues, letter) {
  if (!trainingCategoryValues) return 0;
  const direct = trainingCategoryValues[letter];
  if (direct?.c != null) return Number(direct.c);
  const prefixed = trainingCategoryValues[`Category ${letter}`];
  if (prefixed?.c != null) return Number(prefixed.c);
  for (const [k, v] of Object.entries(trainingCategoryValues)) {
    if (normalizeCategory(k) === letter) return Number(v?.c ?? 0);
  }
  return 0;
}

function resolveDisabilityTarget(sliderMeta = {}) {
  if (sliderMeta.disabilityTotal != null) return Number(sliderMeta.disabilityTotal);
  const dd = sliderMeta.disabilityData;
  if (dd?.total != null) return Number(dd.total) || 0;
  return 0;
}

function resolveUnemployedTarget(sliderMeta = {}) {
  if (sliderMeta.unemployedTotal != null) return Number(sliderMeta.unemployedTotal);
  const ud = sliderMeta.unemployedData;
  if (ud?.c != null) return Number(ud.c) || 0;
  if (ud?.total != null) return Number(ud.total) || 0;
  return 0;
}

/**
 * Recompute allocation_summary from plan + expanded payload (authoritative currents).
 */
export function recomputeAllocationSummary(input, plan, sliderMeta = {}) {
  if (!input || !plan?.employees) return null;

  const master = input.masterTrainingData || [];
  let allocated = 0;
  let disabilityCurrent = 0;
  let unemployedCurrent = 0;
  const categoryCurrents = {};
  const bbeeCurrents = {};

  for (const row of plan.employees || []) {
    const emp = (input.employees || []).find((e) => e.emp_id === row.emp_id);
    const race = String(emp?.race ?? "").toLowerCase();
    const gender = String(emp?.gender ?? "").toLowerCase();
    const isDisabled = Boolean(emp?.disability);
    const isUnemployed = Boolean(emp?.unemployed);
    if (race && !bbeeCurrents[race]) {
      bbeeCurrents[race] = { male: 0, female: 0 };
    }
    for (const tid of row.training_ids || []) {
      const { cost, category: cat } = resolveEmpTrainingCostAndCategory(
        emp,
        tid,
        master,
      );
      allocated += cost;
      if (cat) categoryCurrents[cat] = (categoryCurrents[cat] ?? 0) + cost;
      if (isUnemployed) {
        unemployedCurrent += cost;
        continue;
      }
      if (race && bbeeCurrents[race]) {
        if (gender === "female") bbeeCurrents[race].female += cost;
        else bbeeCurrents[race].male += cost;
      }
      if (isDisabled) disabilityCurrent += cost;
    }
  }

  const total = Number(input.optimizer_config?.total_budget ?? 0);
  const percentage =
    total > 0 ? Math.round((allocated / total) * 10000) / 100 : 0;

  const raceKeys = new Set([
    ...Object.keys(bbeeCurrents),
    ...Object.keys(sliderMeta.bbeeValues || {}).map((k) => k.toLowerCase()),
  ]);
  const raceOrder = ["african", "coloured", "indian", "white", "foreigner"];
  const orderedRaces = [
    ...raceOrder.filter((r) => raceKeys.has(r)),
    ...[...raceKeys].filter((r) => !raceOrder.includes(r) && r !== "disability"),
  ];

  const bbee_segments = orderedRaces.map((race) => {
    const cur = bbeeCurrents[race] || { male: 0, female: 0 };
    const label = race.charAt(0).toUpperCase() + race.slice(1);
    return {
      name: label,
      key: race,
      male: {
        target: lookupBbeeTarget(sliderMeta.bbeeValues, race, "male"),
        current: cur.male,
      },
      female: {
        target: lookupBbeeTarget(sliderMeta.bbeeValues, race, "female"),
        current: cur.female,
      },
    };
  });

  const bbee_totals = bbee_segments.reduce(
    (acc, segment) => {
      acc.maleTarget += segment.male.target || 0;
      acc.maleCurrent += segment.male.current || 0;
      acc.femaleTarget += segment.female.target || 0;
      acc.femaleCurrent += segment.female.current || 0;
      return acc;
    },
    { maleTarget: 0, maleCurrent: 0, femaleTarget: 0, femaleCurrent: 0 },
  );

  const catKeys = new Set([
    ...Object.keys(categoryCurrents),
    ...Object.keys(sliderMeta.trainingCategoryValues || {})
      .map(normalizeCategory)
      .filter(Boolean),
    ...Object.keys(input.optimizer_config?.training_categories || {}),
  ]);
  const training_categories = [...catKeys]
    .filter(Boolean)
    .sort()
    .map((letter) => ({
      name: `Category ${letter}`,
      key: letter,
      target: lookupCategoryTarget(sliderMeta.trainingCategoryValues, letter),
      current: categoryCurrents[letter] ?? 0,
    }));

  const training_category_totals = training_categories.reduce(
    (acc, c) => {
      acc.target += c.target || 0;
      acc.current += c.current || 0;
      return acc;
    },
    { target: 0, current: 0 },
  );

  const disabilityTarget = resolveDisabilityTarget(sliderMeta);
  const unemployedTarget = resolveUnemployedTarget(sliderMeta);

  return {
    budget: { total, allocated, percentage },
    bbee_segments,
    bbee_totals: {
      ...bbee_totals,
      unemployedTarget,
      unemployedCurrent,
      planTarget:
        bbee_totals.maleTarget + bbee_totals.femaleTarget + unemployedTarget,
      planCurrent:
        bbee_totals.maleCurrent + bbee_totals.femaleCurrent + unemployedCurrent,
    },
    training_categories,
    training_category_totals,
    disability: { target: disabilityTarget, current: disabilityCurrent },
    unemployed: { target: unemployedTarget, current: unemployedCurrent },
    bbee_breakdown: Object.fromEntries(
      bbee_segments.map((s) => [s.key, { male: s.male, female: s.female }]),
    ),
    training_category_breakdown: Object.fromEntries(
      training_categories.map((c) => [
        c.key,
        { target: c.target, current: c.current },
      ]),
    ),
    critical_skills_priority: sliderMeta.criticalSkillsPriority,
  };
}
